import { useStore } from '@tanstack/react-form'
import { z } from 'zod'

import type { Patient } from '@snootsvet/shared'
import { dateOfBirthSchema, isEmptyHtml } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { useUpdatePatient } from '@/frontend/hooks/api/patients/use-update-patient'

const updatePatientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  species: z.string().min(1, 'Species is required'),
  // allow an empty breed roundtrip
  breed: z.string().nullable(),
  color: z.string().trim().nullable(),
  sex: z.enum(['male', 'female'], { message: 'Sex is required' }),
  // allow any empty DOB roundtrip
  dateOfBirth: dateOfBirthSchema.nullable(),
  isNeutered: z.boolean().nullable(),
  microchipNumber: z.string().trim().nullable(),
  internalNotes: z.string().nullable(),
})

type UpdatePatientFormValues = z.infer<typeof updatePatientSchema>

const SPECIES_OPTIONS = [
  { label: 'Dog', value: 'Dog' },
  { label: 'Cat', value: 'Cat' },
]

const SEX_OPTIONS = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
]

const NEUTERED_OPTIONS = [
  { label: 'Unknown', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
]

/**
 * Reads a stored date of birth back as the calendar day it names, for the
 * `<input type="date">` this form binds it to.
 *
 * DOB is written as UTC midnight — every write path does `new Date("YYYY-MM-DD")` —
 * so the day has to be taken from the string as written, the same rule
 * `formatCalendarDate` applies. Parsing it into a `Date` and formatting would read
 * the instant in the *browser's* zone, landing on the previous day anywhere west of
 * Greenwich. That is not just a display bug here: the form posts `dateOfBirth` back
 * on every patch, so a wrong pre-fill walks the stored day backwards one day per
 * edit, even when the edit was to the colour field.
 */
export function toDateOfBirthInputValue(dateOfBirth: string | null): string | null {
  return dateOfBirth ? dateOfBirth.slice(0, 10) : null
}

type UpdatePatientFormProps = {
  patient: Patient
  onSuccess?: () => void
  onCancel?: () => void
}

/** Form for editing patient details and internal notes. */
export function UpdatePatientForm({ patient, onSuccess, onCancel }: UpdatePatientFormProps) {
  const { mutateAsync: updatePatient } = useUpdatePatient(patient.id)

  const form = useAppForm({
    defaultValues: {
      name: patient.name,
      species: patient.species,
      breed: patient.breed,
      color: patient.color,
      sex: patient.sex,
      // Default to null (not undefined) for legacy patients with no DOB on record - the
      // form schema is z.iso.date().nullable(), which accepts a date string or null but
      // not undefined, so leaving it as undefined would block the submit before any request
      // fires. The submit handler later coerces falsy values to undefined to omit the field
      // from the PATCH payload entirely.
      dateOfBirth: toDateOfBirthInputValue(patient.dateOfBirth),
      isNeutered: patient.isNeutered,
      microchipNumber: patient.microchipNumber,
      internalNotes: patient.internalNotes,
    } as UpdatePatientFormValues,
    validators: { onSubmit: updatePatientSchema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await updatePatient({
          ...value,
          // breed and DOB can't be cleared once set - the PATCH schema rejects null and the
          // field-level validators above block empty submits, so coerce empty input to
          // undefined (omit from payload) rather than null.
          breed: value.breed || undefined,
          dateOfBirth: value.dateOfBirth || undefined,
          color: value.color || null,
          microchipNumber: value.microchipNumber || null,
          internalNotes: isEmptyHtml(value.internalNotes) ? null : value.internalNotes,
        })
        onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to save patient',
            fields: {},
          },
        })
      }
    },
  })

  // Drive the neutered/spayed label from the live sex value.
  const sex = useStore(form.store, (s) => s.values.sex)
  const neuteredLabel = sex === 'female' ? 'Spayed' : 'Neutered'

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <form.GlobalError />
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          <div className="grid min-w-0 gap-4 sm:grid-cols-3">
            <form.AppField name="name">
              {(field) => (
                <field.InputField label="Name" placeholder="Buddy" layoutClassName="min-w-0" />
              )}
            </form.AppField>
            <form.AppField name="species">
              {(field) => (
                <field.SelectField
                  label="Species"
                  placeholder="Select"
                  items={SPECIES_OPTIONS}
                  layoutClassName="min-w-0"
                />
              )}
            </form.AppField>
            {/*
              Breed is nullable on the PATCH schema so legacy patients without one aren't
              blocked from editing other fields. This validator narrows that: once a patient
              has a breed on record, the edit form won't let staff clear it back to empty.
            */}
            <form.AppField
              name="breed"
              validators={{
                onSubmit: ({ value }) =>
                  patient.breed && !value ? 'Breed is required' : undefined,
              }}
            >
              {(field) => (
                <field.InputField label="Breed" placeholder="Labrador" layoutClassName="min-w-0" />
              )}
            </form.AppField>
            <form.AppField name="sex">
              {(field) => (
                <field.SelectField
                  label="Sex"
                  placeholder="Select"
                  items={SEX_OPTIONS}
                  layoutClassName="min-w-0"
                />
              )}
            </form.AppField>
            <form.AppField name="isNeutered">
              {(field) => (
                <field.SelectField
                  label={neuteredLabel}
                  placeholder="Unknown"
                  items={NEUTERED_OPTIONS}
                  layoutClassName="min-w-0"
                />
              )}
            </form.AppField>
            <form.AppField
              name="dateOfBirth"
              validators={{
                onSubmit: ({ value }) =>
                  patient.dateOfBirth && !value ? 'Date of birth is required' : undefined,
              }}
            >
              {(field) => (
                <field.InputField label="Date of birth" type="date" layoutClassName="min-w-0" />
              )}
            </form.AppField>
            <form.AppField name="microchipNumber">
              {(field) => (
                <field.InputField
                  label="Microchip number"
                  placeholder="982000123456789"
                  layoutClassName="min-w-0"
                />
              )}
            </form.AppField>
            <form.AppField name="color">
              {(field) => (
                <field.InputField
                  label="Colour"
                  placeholder="Black & tan"
                  layoutClassName="min-w-0"
                />
              )}
            </form.AppField>
          </div>
          <form.AppField name="internalNotes">
            {(field) => (
              <field.RichTextField
                label="Internal Notes"
                placeholder="Add internal notes…"
                layoutClassName="min-w-0"
                className="[&_.tiptap]:min-h-64"
              />
            )}
          </form.AppField>
        </div>

        <div className="pt-3 flex items-center gap-x-2 justify-end">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}
